import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppCustomers } from '@/hooks/useAppCustomers';
import {
  isPrivateImage,
  ProductImageApiError,
  productImages,
} from './ProductImages';
import type { ImageDescriptor } from './types';

interface LoadedImage {
  key: string;
  url: string;
  descriptor: ImageDescriptor;
  validUntil: number;
}

export interface RepresentativeImageExportGuard {
  signal: AbortSignal;
  isValid: () => boolean;
}

const decodeImage = (url: string, signal: AbortSignal, expiresAt: number) =>
  new Promise<void>((resolve, reject) => {
    const image = new Image();
    const finish = (error?: Error) => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      image.onload = null;
      image.onerror = null;
      if (error) reject(error);
      else resolve();
    };
    const onAbort = () => finish(new Error('Image request cancelled'));
    const timer = setTimeout(
      () => finish(new Error('Image authorization expired')),
      Math.min(30_000, Math.max(0, expiresAt - Date.now())),
    );
    signal.addEventListener('abort', onAbort, { once: true });
    if (typeof image.decode !== 'function') {
      image.onload = () => finish();
      image.onerror = () => finish(new Error('Invalid image'));
    }
    image.src = url;
    if (typeof image.decode === 'function')
      void image.decode().then(
        () => finish(),
        () => finish(new Error('Invalid image')),
      );
    if (signal.aborted) onAbort();
  });

export const useRepresentativeImage = (vehicleId?: number, enabled = true) => {
  const { customerIds, locationIds, userPermissions } = useAppCustomers();
  const key = JSON.stringify([
    vehicleId,
    customerIds,
    locationIds,
    userPermissions,
  ]);
  const [loaded, setLoaded] = useState<LoadedImage | null>(null);
  const live = useRef<LoadedImage | null>(null);
  const request = useRef<AbortController | null>(null);
  const unavailable = useRef(false);
  const exportAuthorization = useRef(new AbortController());

  const clear = useCallback(() => {
    exportAuthorization.current.abort();
    exportAuthorization.current = new AbortController();
    if (live.current?.url.startsWith('blob:'))
      URL.revokeObjectURL(live.current.url);
    live.current = null;
    setLoaded(null);
  }, []);

  const refresh = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    clear();
    if (!enabled || !vehicleId || document.visibilityState === 'hidden') return;
    let pendingUrl: string | null = null;
    try {
      const descriptor = await productImages.representative(
        vehicleId,
        controller.signal,
      );
      if (
        controller.signal.aborted ||
        !descriptor ||
        descriptor.sourceKind !== 'Generated' ||
        descriptor.representationKind !== 'Outline'
      )
        return;
      const validUntil = Math.min(
        Date.parse(descriptor.expiresAtUtc),
        Date.now() + 60_000,
      );
      if (!Number.isFinite(validUntil) || validUntil <= Date.now()) return;
      if (descriptor.contentUrl && /^https?:\/\//.test(descriptor.contentUrl)) {
        pendingUrl = descriptor.contentUrl;
      } else {
        const blob = await productImages.vehicleContent(
          vehicleId,
          descriptor.assetId,
          controller.signal,
        );
        if (
          controller.signal.aborted ||
          validUntil <= Date.now() ||
          !isPrivateImage(blob)
        )
          return;
        pendingUrl = URL.createObjectURL(blob);
      }
      await decodeImage(pendingUrl, controller.signal, validUntil);
      if (controller.signal.aborted || validUntil <= Date.now()) return;
      const image = { key, descriptor, url: pendingUrl, validUntil };
      live.current = image;
      pendingUrl = null;
      setLoaded(image);
    } catch (error) {
      if (controller.signal.aborted) return;
      clear();
      if (
        error instanceof ProductImageApiError &&
        [403, 404].includes(error.status)
      )
        unavailable.current = true;
    } finally {
      if (pendingUrl?.startsWith('blob:')) URL.revokeObjectURL(pendingUrl);
    }
  }, [clear, enabled, key, vehicleId]);

  const prepareExport =
    useCallback(async (): Promise<RepresentativeImageExportGuard> => {
      await refresh();
      const image = live.current;
      const signal = exportAuthorization.current.signal;
      return {
        signal,
        isValid: () =>
          !signal.aborted &&
          enabled &&
          document.visibilityState !== 'hidden' &&
          (!image ||
            (image.key === key &&
              live.current === image &&
              image.validUntil > Date.now())),
      };
    }, [enabled, key, refresh]);

  useEffect(() => {
    unavailable.current = false;
    void refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        request.current?.abort();
        clear();
      } else if (!unavailable.current) void refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      request.current?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      clear();
    };
  }, [clear, refresh]);

  return {
    image: loaded?.key === key && enabled ? loaded : null,
    refresh,
    prepareExport,
  };
};
